/**
 * Custom fonts API — GET / POST / DELETE
 * Backend: banner-service-node `/api/v1/custom-fonts` (via instanceCS).
 * Contract: docs/apiDocs.md, docs/CUSTOM_FONTS_API.md
 */

import { nodeInstanceCS } from "./axiosConfigCS";

const CUSTOM_FONTS_BASE_PATH = "/api/v1/custom-fonts";
const CUSTOM_FONT_STYLE_ELEMENT_ID = "imc-inline-editor-custom-font-faces";

/**
 * Tracking params for custom-font API calls (banner/template asset context).
 * @param {{ assetInfo?: { assetId?: string, assetName?: string }, assetId?: string, assetName?: string }} [source]
 * @returns {{ assetId?: string, assetName?: string }}
 */
export function getCustomFontTrackingParams(source = {}) {
  const assetInfo = source.assetInfo ?? source;
  const tracking = {};
  const assetId = assetInfo?.assetId ?? source.assetId;
  const assetName = assetInfo?.assetName ?? source.assetName;
  if (assetId) tracking.assetId = assetId;
  if (assetName) tracking.assetName = assetName;
  return tracking;
}

function appendCustomFontTrackingToFormData(formData, tracking = {}) {
  if (tracking.assetId) {
    formData.append("assetId", tracking.assetId);
  }
  if (tracking.assetName) {
    formData.append("assetName", tracking.assetName);
  }
}

/** True when API/marked row is an unresolved duplicate upload. */
export function isDuplicateFontFlag(record) {
  return record?.isDuplicateFont === true;
}

function normalizeCustomFontRecord(record) {
  if (!record || typeof record !== "object") {
    return record;
  }
  const isDuplicate = isDuplicateFontFlag(record);
  return {
    ...record,
    isDuplicateFont: isDuplicate,
    duplicateOfFontId: isDuplicate ? record.duplicateOfFontId : undefined,
  };
}

export function normalizeCustomFontList(fonts = []) {
  return (Array.isArray(fonts) ? fonts : []).map(normalizeCustomFontRecord);
}

function normalizeUploadResultRow(row = {}) {
  if (row.isSaved !== undefined || row.failedToUpload !== undefined) {
    return {
      fileName: row.fileName ?? "file",
      isSaved: Boolean(row.isSaved),
      isDuplicateFont: Boolean(
        row.isDuplicateFont ?? row.font?.isDuplicateFont,
      ),
      failedToUpload: Boolean(row.failedToUpload),
      duplicateOfFontId: row.duplicateOfFontId ?? row.font?.duplicateOfFontId,
      font: row.font ? normalizeCustomFontRecord(row.font) : undefined,
    };
  }

  const failed = row.success === false;
  const isDuplicate = Boolean(row.isDuplicateFont ?? row.font?.isDuplicateFont);

  return {
    fileName: row.fileName ?? "file",
    isSaved: !failed && !isDuplicate,
    isDuplicateFont: isDuplicate,
    failedToUpload: failed,
    duplicateOfFontId: row.duplicateOfFontId ?? row.font?.duplicateOfFontId,
    font: row.font ? normalizeCustomFontRecord(row.font) : undefined,
  };
}

function buildUploadResponse(results) {
  const normalized = results.map(normalizeUploadResultRow);
  const success = normalized.some((r) => r.isSaved || r.isDuplicateFont);
  return { success, results: normalized };
}

/** Normalize legacy single-file API body to batch shape. */
export function normalizeCustomFontUploadResponse(data) {
  if (data?.results && Array.isArray(data.results)) {
    return buildUploadResponse(data.results);
  }

  const isDuplicate = Boolean(
    data?.isDuplicateFont ?? data?.font?.isDuplicateFont,
  );
  const failed = data?.success === false || data?.failedToUpload === true;

  return buildUploadResponse([
    {
      fileName: data?.font?.label ?? "file",
      isSaved: !failed && !isDuplicate,
      isDuplicateFont: isDuplicate,
      failedToUpload: failed,
      duplicateOfFontId: data?.duplicateOfFontId,
      font: data?.font,
    },
  ]);
}

function toFileList(files) {
  if (!files) return [];
  return Array.isArray(files) ? files : [files];
}

function appendFilesToFormData(formData, fileList, { replaceFontId } = {}) {
  if (replaceFontId) {
    formData.append("file", fileList[0]);
    formData.append("replaceFontId", replaceFontId);
    return;
  }
  if (fileList.length === 1) {
    formData.append("file", fileList[0]);
    return;
  }
  fileList.forEach((file) => {
    formData.append("files", file);
  });
}

function requireAssetId(tracking, operation) {
  if (!tracking.assetId) {
    throw new Error(`assetId is required to ${operation} custom fonts`);
  }
}

/**
 * GET /api/v1/custom-fonts
 * @param {{ assetInfo?: { assetId?: string, assetName?: string } }} [params]
 * @returns {Promise<Object[]>} normalized custom font records
 */
export async function fetchCustomFontsFromApi(params = {}) {
  const tracking = getCustomFontTrackingParams(params);
  if (!tracking.assetId) {
    return [];
  }

  const { data } = await nodeInstanceCS.get(CUSTOM_FONTS_BASE_PATH, {
    params: tracking,
  });

  const customFonts = normalizeCustomFontList(data?.fonts);
  injectFontFaceCss(customFonts)
  return customFonts
}

/**
 * POST /api/v1/custom-fonts — upload, batch, or replace (replaceFontId).
 */
export async function uploadCustomFontsToApi(files, options = {}) {
  const fileList = toFileList(files);
  if (fileList.length === 0) {
    throw new Error("No files to upload");
  }

  const { onUploadProgress, ...requestOptions } = options;
  const tracking = getCustomFontTrackingParams(requestOptions);
  requireAssetId(tracking, "upload");

  const formData = new FormData();
  appendFilesToFormData(formData, fileList, requestOptions);
  appendCustomFontTrackingToFormData(formData, tracking);

  const { data } = await nodeInstanceCS.post(CUSTOM_FONTS_BASE_PATH, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onUploadProgress
      ? (event) => {
          if (!event.total) return;
          onUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      : undefined,
  });

  return normalizeCustomFontUploadResponse(data);
}

export async function uploadCustomFontToApi(file, options = {}) {
  return uploadCustomFontsToApi([file], options);
}

/**
 * DELETE /api/v1/custom-fonts/:fontId
 */
export async function deleteCustomFontFromApi(fontId, params = {}) {
  const tracking = getCustomFontTrackingParams(params);
  requireAssetId(tracking, "delete");

  const { data } = await nodeInstanceCS.delete(
    `${CUSTOM_FONTS_BASE_PATH}/${encodeURIComponent(fontId)}`,
    { params: tracking },
  );

  return data;
}

export function getResolvedCustomFonts(fonts = []) {
  return fonts.filter((row) => !isDuplicateFontFlag(row));
}

export function hasUnresolvedCustomFontDuplicate(fonts = []) {
  return fonts.some((row) => isDuplicateFontFlag(row));
}

const injectFontFaceCss = (fontList) => {
  let style = document.getElementById(CUSTOM_FONT_STYLE_ELEMENT_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = CUSTOM_FONT_STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }
  let fontFaceCss = ""
  for (let i = 0; i < fontList.length; i++) {
    const font = fontList[i]
    fontFaceCss += `
    @font-face {
      font-family: '${font.familyName}';
      src: url('${font.dataUrl}') format('${font.format}');
    }`
  }
  style.textContent = fontFaceCss
}